import { DayOfWeek, LocalDate, TemporalAdjusters } from "@js-joda/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useScheduleStore } from "web-apps/company/stores/schedule/scheduleStore";
import {
  formatIsoDate,
  getStockholmLocalDateNow,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import { ScheduleSortBy } from "services/api/company/schedule.service";
import type {
  GroupByOption,
  IntervalOption,
  SizeOption,
} from "web-apps/company/components/schedule/types/schedule.types";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import type { PersonOption } from "../../utils/schedule.utils.types";
import { useCompanyFilterOptions } from "web-apps/company/components/common/hooks/useCompanyFilterOptions";

export type {
  GroupByOption,
  IntervalOption,
  SizeOption,
} from "web-apps/company/components/schedule/types/schedule.types";

export interface UseScheduleToolbarFiltersState {
  isPreferencesReady: boolean;
  groupByOption: GroupByOption;
  intervalOption: IntervalOption;
  sizeOption: SizeOption;
  searchValue: string;
  searchTags: string[];
  weekDate: LocalDate;
  monthDate: LocalDate;
  range: { fromDate: string; toDate: string };
  pageSize: number;
  sortBy: ScheduleSortBy;
  workGroupIds: number[];
  workerIds: number[];
  unitTeamOptions: UnitTeamOption[];
  selectedWorkGroupIds: string[];
  personOptions: PersonOption[];
  workersLoadError: boolean;
  unitTeamLoadError: boolean;
  selectedPersonIds: string[];
  setGroupByOption: (value: GroupByOption) => void;
  setIntervalOption: (value: IntervalOption) => void;
  setSizeOption: (value: SizeOption) => void;
  setSearchValue: (value: string) => void;
  setSearchTags: (value: string[]) => void;
  setWeekDate: (value: LocalDate) => void;
  setMonthDate: (value: LocalDate) => void;
  setSelectedWorkGroupIds: (values: string[]) => void;
  setSelectedPersonIds: (values: string[]) => void;
}

const PAGE_SIZE = 50;

/**
 * Builds the active date range based on interval and selected dates.
 */
const buildRange = (
  intervalOption: IntervalOption,
  weekDate: LocalDate,
  monthDate: LocalDate,
): { fromDate: string; toDate: string } => {
  if (intervalOption === "weekly") {
    const start = weekDate.with(
      TemporalAdjusters.previousOrSame(DayOfWeek.MONDAY),
    );
    const end = start.plusDays(6);
    return {
      fromDate: formatIsoDate(start),
      toDate: formatIsoDate(end),
    };
  }

  const start = monthDate.withDayOfMonth(1);
  const end = monthDate.withDayOfMonth(monthDate.lengthOfMonth());
  return {
    fromDate: formatIsoDate(start),
    toDate: formatIsoDate(end),
  };
};

/**
 * Schedule toolbar filter hook that manages range, grouping, and size options.
 */
export const useScheduleToolbarFilters = (): UseScheduleToolbarFiltersState => {
  const [companyState] = useCompanyStore();
  const [scheduleState, scheduleActions] = useScheduleStore();
  const companyId = companyState.company?.id;

  const lastLoadedCompanyIdRef = useRef<number | null>(null);
  const skipPersistCompanyIdRef = useRef<number | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [searchTags, setSearchTags] = useState<string[]>([]);
  const [weekDate, setWeekDate] = useState(getStockholmLocalDateNow());
  const [monthDate, setMonthDate] = useState(getStockholmLocalDateNow());
  const [selectedWorkGroupIds, setSelectedWorkGroupIds] = useState<string[]>(
    [],
  );
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const filterOptions = useCompanyFilterOptions({
    companyId,
    setSelectedWorkGroupIds,
  });
  const [isPreferencesReady, setIsPreferencesReady] = useState(false);

  const groupByOption = scheduleState.preferences.groupByOption;
  const intervalOption = scheduleState.preferences.intervalOption;
  const sizeOption = scheduleState.preferences.sizeOption;

  const range = useMemo(
    () => buildRange(intervalOption, weekDate, monthDate),
    [intervalOption, monthDate, weekDate],
  );

  const workGroupIds = useMemo(() => {
    return selectedWorkGroupIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
  }, [selectedWorkGroupIds]);

  const workerIds = useMemo(() => {
    return selectedPersonIds
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
  }, [selectedPersonIds]);

  const sortBy = useMemo(
    () => (groupByOption === "team" ? "workGroup" : "worker"),
    [groupByOption],
  );

  useEffect(() => {
    let isCancelled = false;
    setIsPreferencesReady(false);
    if (!companyId) {
      lastLoadedCompanyIdRef.current = null;
      skipPersistCompanyIdRef.current = null;
      return;
    }
    lastLoadedCompanyIdRef.current = companyId;
    skipPersistCompanyIdRef.current = companyId;
    scheduleActions.loadSchedulePreferences(companyId);
    if (isCancelled) return;
    if (lastLoadedCompanyIdRef.current !== companyId) return;
    setIsPreferencesReady(true);
    return (): void => {
      isCancelled = true;
    };
  }, [companyId, scheduleActions]);

  useEffect(() => {
    if (!companyId) return;
    if (lastLoadedCompanyIdRef.current !== companyId) return;
    if (skipPersistCompanyIdRef.current === companyId) {
      skipPersistCompanyIdRef.current = null;
      return;
    }
    scheduleActions.persistSchedulePreferences(
      companyId,
      scheduleState.preferences,
    );
  }, [companyId, scheduleActions, scheduleState.preferences]);

  const updatePreferences = useCallback(
    (
      next: Partial<{
        groupByOption: GroupByOption;
        intervalOption: IntervalOption;
        sizeOption: SizeOption;
      }>,
    ): void => {
      scheduleActions.patchSchedulePreferences(next);
    },
    [scheduleActions],
  );

  const setGroupByOption = useCallback(
    (value: GroupByOption): void => {
      updatePreferences({ groupByOption: value });
    },
    [updatePreferences],
  );

  const setIntervalOption = useCallback(
    (value: IntervalOption): void => {
      updatePreferences({ intervalOption: value });
    },
    [updatePreferences],
  );

  const setSizeOption = useCallback(
    (value: SizeOption): void => {
      updatePreferences({ sizeOption: value });
    },
    [updatePreferences],
  );

  return {
    isPreferencesReady,
    groupByOption,
    intervalOption,
    sizeOption,
    searchValue,
    searchTags,
    weekDate,
    monthDate,
    range,
    pageSize: PAGE_SIZE,
    sortBy,
    workGroupIds,
    workerIds,
    unitTeamOptions: filterOptions.unitTeamOptions,
    selectedWorkGroupIds,
    personOptions: filterOptions.personOptions,
    workersLoadError: filterOptions.workersLoadError,
    unitTeamLoadError: filterOptions.unitTeamLoadError,
    selectedPersonIds,
    setGroupByOption,
    setIntervalOption,
    setSizeOption,
    setSearchValue,
    setSearchTags,
    setWeekDate,
    setMonthDate,
    setSelectedWorkGroupIds,
    setSelectedPersonIds,
  };
};
